import { useMutation } from "@apollo/client";
import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  GetAccountQuery,
  SendAccountPhoneNumberVerificationCodeMutation,
  SendAccountPhoneNumberVerificationCodeMutationVariables,
  UpdateUserSettingsMutation,
  UpdateUserSettingsMutationVariables,
  VerifyAccountPhoneNumberMutation,
  VerifyAccountPhoneNumberMutationVariables,
} from "types/graphql-schema";

import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Input from "@components/input/input";
import Link from "@components/link/link";
import Loading from "@components/loading/loading";
import { assertEdgesNonNull } from "@helpers/helpers";

import sendPhoneNumberVerificationCodeMutation from "../../graphql/send-phone-number-verification-code-mutation";
import updateUserMutation from "../../graphql/update-user-mutation";
import verifyPhoneNumberMutation from "../../graphql/verify-phone-number-mutation";

const PhoneNumber = ({
  me,
  refetch,
}: {
  me: NonNullable<GetAccountQuery["me"]>;
  refetch: () => void;
}) => {
  const organizations = useMemo(
    () =>
      assertEdgesNonNull(me.organizations).filter(
        (organization) => !organization.personal && organization.userMembership,
      ),
    [me.organizations],
  );
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<
    number | null
  >(organizations[0]?.id ?? null);
  const selectedOrganization = organizations.find(
    (organization) => organization.id === selectedOrganizationId,
  );
  const selectedMembership = selectedOrganization?.userMembership;
  const savedPhoneNumber = selectedMembership?.phoneNumber || "";
  const savedMessagingConsent = !!selectedMembership?.phoneMessagingConsent;
  const [phoneNumber, setPhoneNumber] = useState(savedPhoneNumber);
  const [messagingConsent, setMessagingConsent] = useState(
    savedMessagingConsent,
  );
  const [verificationCode, setVerificationCode] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [updateUser, { loading: savingPhoneNumber }] = useMutation<
    UpdateUserSettingsMutation,
    UpdateUserSettingsMutationVariables
  >(updateUserMutation);
  const [sendVerificationCode, { loading: sendingVerificationCode }] =
    useMutation<
      SendAccountPhoneNumberVerificationCodeMutation,
      SendAccountPhoneNumberVerificationCodeMutationVariables
    >(sendPhoneNumberVerificationCodeMutation);
  const [verifyPhoneNumber, { loading: verifyingPhoneNumber }] = useMutation<
    VerifyAccountPhoneNumberMutation,
    VerifyAccountPhoneNumberMutationVariables
  >(verifyPhoneNumberMutation);

  useEffect(() => {
    setPhoneNumber(savedPhoneNumber);
    setMessagingConsent(savedMessagingConsent);
    setVerificationCode("");
    setStatusMessage("");
    setErrorMessage("");
  }, [savedPhoneNumber, savedMessagingConsent, selectedOrganizationId]);

  useEffect(() => {
    if (
      !selectedOrganizationId ||
      !organizations.some(
        (organization) => organization.id === selectedOrganizationId,
      )
    ) {
      setSelectedOrganizationId(organizations[0]?.id ?? null);
    }
  }, [organizations, selectedOrganizationId]);

  const loading =
    savingPhoneNumber || sendingVerificationCode || verifyingPhoneNumber;
  const phoneNumberChanged = phoneNumber.trim() !== savedPhoneNumber;
  const messagingConsentRequired =
    phoneNumberChanged && !!phoneNumber.trim() && !messagingConsent;
  const messagingConsentChanged = messagingConsent !== savedMessagingConsent;
  const settingsChanged = phoneNumberChanged || messagingConsentChanged;
  const phoneNumberIsVerified = !!selectedMembership?.phoneNumberVerified;
  const verificationPending = !!savedPhoneNumber && !phoneNumberIsVerified;

  const handleSavePhoneNumber = async (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (
      !settingsChanged ||
      messagingConsentRequired ||
      loading ||
      !selectedOrganizationId
    ) {
      return;
    }

    setErrorMessage("");
    setStatusMessage("");
    try {
      await updateUser({
        context: { skipGraphqlErrorHandling: true },
        variables: {
          userId: me.id,
          phoneNumberOrganizationId: selectedOrganizationId,
          phoneNumber: phoneNumber.trim(),
          phoneMessagingConsent: !!phoneNumber.trim() && messagingConsent,
        },
      });
      setVerificationCode("");
      setStatusMessage(
        phoneNumberChanged
          ? phoneNumber.trim()
            ? "Verification code sent."
            : "Phone number removed."
          : "Text messaging preference saved.",
      );
      refetch();
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Phone number could not be saved.",
      );
    }
  };

  const handleCancel = () => {
    setPhoneNumber(savedPhoneNumber);
    setMessagingConsent(savedMessagingConsent);
    setErrorMessage("");
    setStatusMessage("");
  };

  const handleResendCode = async () => {
    if (!selectedOrganizationId) {
      return;
    }

    setErrorMessage("");
    setStatusMessage("");
    try {
      await sendVerificationCode({
        variables: { organizationId: selectedOrganizationId },
      });
      setStatusMessage("Verification code sent.");
      refetch();
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Verification code could not be sent.",
      );
    }
  };

  const handleVerifyPhoneNumber = async (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (!verificationCode.trim() || loading || !selectedOrganizationId) {
      return;
    }

    setErrorMessage("");
    setStatusMessage("");
    try {
      await verifyPhoneNumber({
        variables: {
          organizationId: selectedOrganizationId,
          code: verificationCode.trim(),
        },
      });
      setVerificationCode("");
      setStatusMessage("Phone number verified.");
      refetch();
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Phone number could not be verified.",
      );
    }
  };

  if (!selectedMembership) {
    return null;
  }

  return (
    <div className="space-y-4">
      {organizations.length > 1 && (
        <div className="w-full sm:max-w-xs">
          <label
            className="mb-1 block text-sm font-medium text-gray-700"
            htmlFor="phone-number-organization"
          >
            Organization
          </label>
          <select
            id="phone-number-organization"
            aria-label="Phone number organization"
            className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-xs focus:border-blue-500 focus:outline-hidden focus:ring-1 focus:ring-blue-500"
            value={selectedOrganizationId ?? ""}
            onChange={(e) => setSelectedOrganizationId(Number(e.target.value))}
            disabled={loading}
          >
            {organizations.map((organization) => (
              <option key={organization.id} value={organization.id}>
                {organization.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <form onSubmit={handleSavePhoneNumber} className="space-y-3">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
          <div className="w-full sm:max-w-xs">
            <Input
              label="Phone number"
              aria-label="Account phone number input"
              type="tel"
              autoComplete="tel"
              placeholder="+15551234567"
              value={phoneNumber}
              onChange={(e) => {
                setPhoneNumber(e.target.value);
                setMessagingConsent(false);
              }}
              disabled={loading}
            />
          </div>
          <div className="flex min-h-10 items-center gap-2">
            {settingsChanged ? (
              <>
                <Button
                  type="submit"
                  text="Save"
                  theme={buttonTheme.primary}
                  disabled={loading || messagingConsentRequired}
                  tooltip={
                    messagingConsentRequired &&
                    "Check the messaging consent checkbox before saving your phone number."
                  }
                  aria-label="Save phone number button"
                />
                <Button
                  type="button"
                  text="Cancel"
                  disabled={loading}
                  onClick={handleCancel}
                />
              </>
            ) : savedPhoneNumber ? (
              <span
                className={
                  phoneNumberIsVerified
                    ? "rounded-sm bg-green-100 px-2 py-1 text-sm font-medium text-green-700"
                    : "rounded-sm bg-yellow-100 px-2 py-1 text-sm font-medium text-yellow-700"
                }
              >
                {phoneNumberIsVerified ? "Verified" : "Unverified"}
              </span>
            ) : null}
            {loading && <Loading size={5} />}
          </div>
        </div>
        <p className="text-sm text-gray-600">
          Saving a new phone number requires messaging consent and sends an SMS
          verification code.
        </p>
        <div className="max-w-xl space-y-2 text-sm">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-1 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={messagingConsent}
              onChange={(e) => setMessagingConsent(e.target.checked)}
              disabled={loading || !phoneNumber.trim()}
              aria-describedby="phone-messaging-consent-details"
            />
            <span>
              I agree to receive performance-review requests, reminders and
              conversational replies from Topicflow by SMS/RCS.
            </span>
          </label>
          <p id="phone-messaging-consent-details" className="text-gray-600">
            Message frequency varies. Message and data rates may apply. Reply
            STOP to opt out or HELP for help. See our{" "}
            <Link
              to={{
                pathname: "https://www.topicflow.com/terms-of-service-saas/",
              }}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              to={{ pathname: "https://www.topicflow.com/privacy-policy/" }}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </form>

      {verificationPending && !settingsChanged && (
        <form
          onSubmit={handleVerifyPhoneNumber}
          className="flex flex-col gap-4 sm:flex-row sm:items-end"
        >
          <div className="w-full sm:max-w-40">
            <Input
              label="Verification code"
              aria-label="Phone verification code input"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="flex min-h-10 items-center gap-2">
            <Button
              type="submit"
              text="Verify"
              theme={buttonTheme.primary}
              disabled={loading || !verificationCode.trim()}
              aria-label="Verify phone number button"
            />
            <Button
              type="button"
              text="Resend"
              disabled={loading}
              onClick={handleResendCode}
            />
          </div>
        </form>
      )}

      {statusMessage && (
        <div className="text-sm font-medium text-green-700">
          {statusMessage}
        </div>
      )}
      {errorMessage && (
        <div className="text-sm font-medium text-red-700">{errorMessage}</div>
      )}
    </div>
  );
};

export default PhoneNumber;
